/**
 * Cartao de firmware do aparelho selecionado: versao, atualizacao e progresso.
 *
 * Responsabilidade: mostrar em que versao o aparelho esta, oferecer a mais
 * nova que ELE pode instalar, e — enquanto a instalacao acontece — repetir o
 * que o proprio aparelho esta dizendo.
 *
 * NAO e responsabilidade deste arquivo: decidir se uma release serve. Esse
 * filtro e do servidor (`app/services/firmware.py::motivo_de_recusa`), e a
 * lista que chega em `GET .../firmware` ja vem filtrada — board certo, sem
 * release retirada, sem downgrade, sem `min_from` fora de alcance. Refiltrar
 * aqui seria uma segunda copia da regra, e ela divergiria da primeira no
 * criterio seguinte. Tampouco e responsabilidade daqui publicar release: isso
 * e `api/scripts/publish_firmware.py`, no container.
 *
 * ## Esta tela nao verifica nada
 *
 * A nuvem nao esta na cadeia de confianca da imagem — ela decide QUANDO e PARA
 * QUEM, nunca O QUE. Se a imagem nao for assinada pela chave de release, o
 * aparelho a recusa no `esp_ota_end()` e continua na versao antiga; nada nesta
 * tela participa disso, e nenhum texto daqui deve sugerir que participa.
 *
 * ## Por que o progresso vem do aparelho e nao da nuvem
 *
 * A nuvem sabe que assinou uma URL, e nada depois disso: quem baixa, confere o
 * sha256, grava a particao e reinicia e o `cloud_ota` do `gfc`. Por isso a
 * barra de estado deste cartao le o bloco `ota` do estado retido (§5) e nao um
 * campo da API — e por isso ela e HONESTA por construcao: quando o aparelho
 * fica sem conexao no meio do download, o bloco simplesmente para de chegar, e
 * a barra de estado do topo ja diz de quando e o que esta na tela.
 *
 * ## `revertido` aparece como aviso, e nao como erro silencioso
 *
 * Uma imagem que boota e reprova no health check e revertida sozinha pelo
 * bootloader, e o aparelho volta na versao anterior funcionando. Isso e um
 * SUCESSO do mecanismo de rollback e um FRACASSO daquela release, e as duas
 * coisas precisam estar na tela: sem isto o usuario clicaria "Atualizar" de
 * novo, veria o aparelho voltar ao normal, e nunca saberia por que a versao
 * nao mudava. Mesma regra do `pulados_total` do uploader: quem pula, conta.
 */

import * as acoes from "./../acoes";
import * as store from "./../store";
import type { AparelhoNoSnapshot, ReleaseOut } from "./../tipos";
import { el, trocar, type Painel } from "./../ui";

/** `1.2.0` e `v1.2.0` sao a mesma versao — ver `store.mesmaVersao`. */
const semV = (versao: string): string => versao.replace(/^v/, "");

export function montar(): Painel {
  // Ids estaveis: sao o que o gate da C9 usa para afirmar o que esta escrito
  // aqui. Uma checagem por posicao (`p:nth-child(2)`) passaria a medir outra
  // linha no dia em que uma frase entrasse no meio.
  const instalada = el("p", { class: "conta-email", id: "firmware-instalada", text: "—" });
  const situacao = el("p", { class: "hint", id: "firmware-situacao" });
  const aviso = el("p", { class: "hint aviso-perda", id: "firmware-revertido", hidden: true });
  const notas = el("p", { class: "hint", id: "firmware-notas", hidden: true });

  const botao = el("button", {
    type: "button",
    class: "primary small",
    id: "firmware-atualizar",
    text: "Atualizar",
    hidden: true,
  });

  botao.addEventListener("click", () => {
    const alvo = store.aparelhoSelecionado();
    const proxima = alvo === null ? null : maisNova(alvo);
    if (alvo === null || proxima === null) return;
    void acoes.atualizarFirmware(alvo.device.id, proxima.version);
  });

  const raiz = el(
    "section",
    { class: "panel card", id: "firmware" },
    el(
      "div",
      { class: "panel-head" },
      el("span", { class: "panel-title", text: "Firmware" }),
      botao,
    ),
    instalada,
    situacao,
    notas,
    aviso,
  );

  function atualizar(): void {
    const alvo = store.aparelhoSelecionado();
    if (alvo === null) {
      instalada.textContent = "—";
      situacao.textContent = "Nenhum aparelho selecionado.";
      botao.hidden = true;
      notas.hidden = true;
      aviso.hidden = true;
      return;
    }

    instalada.textContent = alvo.device.fw_version ?? "versão desconhecida";

    desenharReversao(aviso, alvo);

    const emCurso = progresso(alvo);
    if (emCurso !== null) {
      situacao.textContent = emCurso;
      botao.hidden = true;
      notas.hidden = true;
      return;
    }

    const proxima = maisNova(alvo);
    if (proxima === null) {
      situacao.textContent =
        alvo.device.fw_version === null
          ? "O aparelho ainda não informou a versão que está rodando."
          : "Este aparelho está na versão mais recente disponível para a placa dele.";
      botao.hidden = true;
      notas.hidden = true;
      return;
    }

    situacao.textContent = `Atualização disponível: ${proxima.version}`;
    notas.hidden = proxima.notes === "";
    notas.textContent = proxima.notes;
    botao.hidden = false;
    botao.textContent = `Atualizar para ${proxima.version}`;
    // Offline nao e impedimento tecnico — o EMQX guarda o comando em QoS1 e
    // entrega quando o aparelho voltar (§3) — mas oferecer o botao sem dizer
    // isso faria a tela parecer travada por dias. Melhor deixar claro.
    botao.disabled = !alvo.device.online;
    if (alvo.device.online) {
      botao.removeAttribute("title");
    } else {
      botao.title = "O aparelho está sem conexão.";
    }
  }

  return { raiz, atualizar };
}

/** A release oferecida: a primeira da lista, que o servidor ja ordenou. */
function maisNova(alvo: AparelhoNoSnapshot): ReleaseOut | null {
  return store.obter().releases[alvo.device.id]?.[0] ?? null;
}

/**
 * A frase de progresso, ou `null` quando nao ha nada em curso.
 *
 * Tres fontes, nesta ordem — da mais informada para a menos:
 *
 * 1. O bloco `ota` do estado retido, que e o aparelho falando de si.
 * 2. O `atualizando` do store: o comando saiu e o aparelho ainda nao respondeu.
 * 3. Nada.
 */
function progresso(alvo: AparelhoNoSnapshot): string | null {
  const ota = alvo.state?.ota;
  switch (ota?.state) {
    case "baixando": {
      const pct = typeof ota.pct === "number" ? ota.pct : 0;
      return `Instalando ${ota.target ?? ""}… baixando ${pct}%`.replace("  ", " ");
    }
    case "gravando":
      return "Instalando… gravando a imagem no aparelho.";
    case "pendente":
      return "Imagem gravada. O aparelho vai reiniciar.";
    // Os 10 min de janela do health check, com a imagem nova JA rodando e o
    // rollback do bootloader ainda armado. Antes desta linha a tela dizia
    // nada, porque o firmware reportava `idle` — o buraco de 600 s da
    // docs/PLANO-ota-confirmando.md. O texto diz que nao ha nada a fazer, que
    // e a unica coisa acionavel aqui: mexer no aparelho durante a janela e o
    // que pode reprovar uma imagem boa.
    case "confirmando":
      return "Instalada. Confirmando a imagem — o aparelho reverte sozinho se algo falhar.";
    default:
      break;
  }
  const pedida = store.obter().atualizando[alvo.device.id];
  if (pedida != null) {
    return `Atualização para ${pedida} enviada. Aguardando o aparelho começar…`;
  }
  return null;
}

/**
 * O aviso de reversao. So quando o aparelho AINDA esta na versao de onde saiu.
 *
 * O bloco `ota` do §5 e retido e nao se apaga sozinho: um `revertido` de tres
 * semanas atras continuaria ali depois de uma atualizacao bem-sucedida. Por
 * isso o aviso some assim que a versao em execucao deixa de ser aquela — o
 * mesmo criterio que a tela usa para saber que uma atualizacao convergiu.
 */
function desenharReversao(aviso: HTMLElement, alvo: AparelhoNoSnapshot): void {
  const ota = alvo.state?.ota;
  const revertida = ota?.state === "revertido" ? ota.target : undefined;
  const rodando = alvo.device.fw_version;
  if (
    revertida === undefined ||
    revertida === "" ||
    (rodando !== null && semV(rodando) === semV(revertida))
  ) {
    aviso.hidden = true;
    return;
  }
  aviso.hidden = false;
  trocar(
    aviso,
    `A versão ${revertida} não passou na verificação depois de instalar, e o ` +
      "aparelho voltou sozinho para a versão anterior. Ele continua " +
      "controlando a estufa normalmente.",
  );
}
